/**
 * Wizard de inventario (épica C1: RES-35, RES-36, RES-37, RES-38). Ruta `/inventory/setup`.
 *
 *   0. Alcance        → solo si la organización tiene más de un local: a cuáles se carga stock
 *   1. Insumos        → rubro con insumos precargados, propios e importación CSV
 *   2. Reventa        → bebidas y envasados detectados en la carta (vínculo + insumo espejo)
 *   3. Recetas        → recetas rápidas de lo más vendido, con sugerencias por rubro
 *   4. Stock inicial  → stock por local y mínimo por insumo, con CSV
 *   5. Políticas      → cuándo descontamos, reponer al cancelar, stock negativo, costo
 *   6. Resumen        → "Activar inventario": `POST /inventory/setup` y después la carta
 *
 * El avance se guarda como borrador (storage, 7 días) y se retoma al volver. El alta masiva es
 * idempotente por nombre, así que reintentar no duplica. Los vínculos y las recetas se graban
 * en la carta producto por producto con barra de progreso y reintento por ítem.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Beef,
  Check,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Croissant,
  Fish,
  IceCreamCone,
  Lock,
  Pizza,
  Plus,
  RotateCcw,
  Sandwich,
  Search,
  Trash2,
  Upload,
  Wand2,
  Wine,
} from 'lucide-react';
import {
  getMenu,
  getInventoryCatalog,
  getInventorySettings,
  listMyRestaurants,
  setupInventory,
  updateItem,
} from '../services/api';
import { storage } from '../utils/storage';
import { trackEvent } from '../utils/analytics';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import { CANONICAL_UNITS, familyUnits } from '../utils/units';
import { DEDUCTION_TRIGGERS, COSTING_METHODS } from '../utils/inventorySettings';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import useEscapeKey from '../hooks/useEscapeKey';
import useFeatureGate from '../hooks/useFeatureGate';
import TemplatePicker from '../components/TemplatePicker';
import { INVENTORY_TEMPLATES } from '../data/inventoryTemplates';
import {
  WIZARD_STEPS,
  EXPANDED_RECIPES,
  MIRROR_TARGET,
  initialWizardState,
  wizardDraftKey,
  serializeDraft,
  parseDraft,
  hasWizardProgress,
  makeWizardItem,
  mergeWizardItems,
  templateItems,
  filterWizardItems,
  groupByCategory,
  parseCatalogCsv,
  parseStockCsv,
  flattenMenu,
  detectResale,
  hasInventoryLink,
  hasRecipe,
  recipeCandidates,
  findRecipeSuggestion,
  applySuggestion,
  ingredientOptions,
  catalogToCreate,
  buildSetupPayload,
  buildMenuOperations,
  summarizeWizard,
  normalizeText,
} from '../utils/inventoryWizard';
import './InventorySetupWizard.css';

const RUBRO_ICONS = { Pizza, Beef, Coffee, Wine, Sandwich, Fish, IceCreamCone, Croissant };
const POLICY_KEYS = ['deductionTrigger', 'restockOnCancel', 'allowNegativeStock', 'costingMethod'];
const KINDS = ['INGREDIENT', 'RESALE', 'PACKAGING'];

/** Políticas del paso 5 a partir de lo efectivo que devuelve `GET /inventory/settings`. */
const settingsFromView = (view) => {
  const effective = (view && view.effective) || {};
  return {
    deductionTrigger: effective.deductionTrigger || 'order_created',
    restockOnCancel: effective.restockOnCancel !== false,
    allowNegativeStock: effective.allowNegativeStock !== false,
    costingMethod: effective.costingMethod || 'average',
  };
};

/** Lee un archivo como texto. */
const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsText(file);
  });

function InventorySetupWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const organizationId = storage.get('selectedOrganizationId');
  const currentRestaurantId = storage.get('selectedRestaurantId');
  const draftKey = wizardDraftKey(organizationId);
  const recipesGate = useFeatureGate('recipesEnabled');

  const [restoredDraft] = useState(() => parseDraft(storage.get(draftKey)));
  const [state, setState] = useState(() => restoredDraft || initialWizardState());
  const [showDraftBanner, setShowDraftBanner] = useState(Boolean(restoredDraft));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [settingsView, setSettingsView] = useState(null);

  const [itemSearch, setItemSearch] = useState('');
  const [customItem, setCustomItem] = useState(null);
  const [csvMessage, setCsvMessage] = useState(null);
  const [resaleSearch, setResaleSearch] = useState('');
  const [showAllRecipes, setShowAllRecipes] = useState(false);
  const [run, setRun] = useState(null);
  const itemsCsvRef = useRef(null);
  const stockCsvRef = useRef(null);

  const isChain = restaurants.length > 1;
  const visibleSteps = useMemo(
    () =>
      WIZARD_STEPS.map((key, index) => ({ key, index })).filter(
        (s) => isChain || s.key !== 'scope'
      ),
    [isChain]
  );
  const stepKey = WIZARD_STEPS[state.step];
  const finished = Boolean(run && run.done && run.allOk);
  const { showModal, confirmNavigation, cancelNavigation, guardNavigation } = useUnsavedChanges(
    hasWizardProgress(state) && !finished
  );
  useEscapeKey(showModal, cancelNavigation);
  useEscapeKey(Boolean(customItem), () => setCustomItem(null));

  const update = useCallback((patch) => {
    setState((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  }, []);

  // ─── Carga ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [restData, menuData, catalogData, settingsData] = await Promise.all([
          organizationId ? listMyRestaurants(organizationId).catch(() => null) : null,
          getMenu(),
          getInventoryCatalog().catch(() => ({ items: [] })),
          getInventorySettings().catch(() => null),
        ]);
        if (cancelled) return;
        const active = ((restData && restData.restaurants) || []).filter(
          (r) => r.status !== 'deleted'
        );
        const list = active.length
          ? active
          : [{ restaurantId: currentRestaurantId, name: storage.get('selectedRestaurantName') }];
        const flat = flattenMenu(menuData && menuData.menu);
        setRestaurants(list);
        setProducts(flat);
        setCatalog((catalogData && catalogData.items) || []);
        setSettingsView(settingsData);
        setState((prev) => {
          if (restoredDraft) return prev;
          const ids = list.map((r) => r.restaurantId).filter(Boolean);
          return {
            ...prev,
            // Con un solo local el paso 0 no aparece
            step: ids.length > 1 ? 0 : 1,
            restaurantIds: ids.length > 1 ? ids : [currentRestaurantId || ids[0]].filter(Boolean),
            resale: detectResale(flat),
            settings: settingsFromView(settingsData),
          };
        });
      } catch (error) {
        console.error('[InventoryWizard] load failed:', error);
        if (!cancelled) setLoadError(t('inventory.wizard.errors.load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackEvent('inventory_wizard_started', { resumed: Boolean(restoredDraft) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Borrador: se guarda en cada cambio mientras no se haya terminado
  useEffect(() => {
    if (loading || finished) return;
    if (hasWizardProgress(state)) storage.set(draftKey, serializeDraft(state));
  }, [state, loading, finished, draftKey]);

  const startOver = () => {
    storage.remove(draftKey);
    const ids = restaurants.map((r) => r.restaurantId).filter(Boolean);
    setState({
      ...initialWizardState(),
      step: isChain ? 0 : 1,
      restaurantIds: isChain ? ids : [currentRestaurantId || ids[0]].filter(Boolean),
      resale: detectResale(products),
      settings: settingsFromView(settingsView),
    });
    setShowDraftBanner(false);
    setRun(null);
  };

  // ─── Derivados ────────────────────────────────────────────────────────────
  const toCreate = useMemo(() => catalogToCreate(state, products), [state, products]);
  const options = useMemo(() => ingredientOptions(toCreate, catalog), [toCreate, catalog]);
  const optionByRef = useMemo(() => new Map(options.map((o) => [o.ref, o])), [options]);
  const candidates = useMemo(
    () => recipeCandidates(products, state.resale),
    [products, state.resale]
  );
  const summary = useMemo(
    () => summarizeWizard(state, products, catalog),
    [state, products, catalog]
  );
  const restaurantName = (rid) =>
    (restaurants.find((r) => r.restaurantId === rid) || {}).name || rid;
  const canEditPolicies = Boolean(
    settingsView && settingsView.can && settingsView.can.settingsWrite
  );
  const catalogKeys = useMemo(() => new Set(catalog.map((c) => normalizeText(c.name))), [catalog]);

  // ─── Navegación ───────────────────────────────────────────────────────────
  const stepError = () => {
    if (stepKey === 'scope' && !state.restaurantIds.length)
      return t('inventory.wizard.scope.required');
    if (stepKey === 'items' && !state.items.some((i) => i.selected)) {
      return t('inventory.wizard.items.required');
    }
    return '';
  };
  const [navError, setNavError] = useState('');
  const goTo = (index) => {
    setNavError('');
    update({ step: index });
    window.scrollTo?.(0, 0);
  };
  const next = () => {
    const error = stepError();
    if (error) {
      setNavError(error);
      return;
    }
    trackEvent('inventory_wizard_step_completed', { step: stepKey });
    const position = visibleSteps.findIndex((s) => s.index === state.step);
    const target = visibleSteps[position + 1];
    if (target) goTo(target.index);
  };
  const back = () => {
    const position = visibleSteps.findIndex((s) => s.index === state.step);
    const target = visibleSteps[position - 1];
    if (target) goTo(target.index);
  };
  const exit = () => guardNavigation(() => navigate('/inventory'));

  // ─── Paso 1: insumos ──────────────────────────────────────────────────────
  const chooseRubro = (rubroId) => {
    update((prev) => ({ rubroId, items: mergeWizardItems(prev.items, templateItems(rubroId)) }));
  };
  const toggleItem = (key) =>
    update((prev) => ({
      items: prev.items.map((i) => (i.key === key ? { ...i, selected: !i.selected } : i)),
    }));
  const setAllItems = (selected) =>
    update((prev) => ({ items: prev.items.map((i) => ({ ...i, selected })) }));
  const saveCustomItem = () => {
    if (!customItem || !customItem.name.trim()) return;
    update((prev) => ({
      items: mergeWizardItems(prev.items, [makeWizardItem(customItem, 'custom')]),
    }));
    setCustomItem(null);
  };
  const importItemsCsv = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const { items, errors } = parseCatalogCsv(await readFile(file));
    update((prev) => ({ items: mergeWizardItems(prev.items, items) }));
    setCsvMessage({ scope: 'items', imported: items.length, errors });
  };

  // ─── Paso 2: reventa ──────────────────────────────────────────────────────
  const setResale = (productId, patch) =>
    update((prev) => ({
      resale: {
        ...prev.resale,
        [productId]: {
          selected: true,
          quantity: 1,
          target: MIRROR_TARGET,
          ...(prev.resale[productId] || {}),
          ...patch,
        },
      },
    }));
  const resaleProducts = useMemo(() => {
    const listed = products.filter((p) => state.resale[p.itemId] || hasInventoryLink(p));
    return listed.sort((a, b) => a.order - b.order);
  }, [products, state.resale]);
  const resaleMatches = useMemo(() => {
    const q = normalizeText(resaleSearch);
    if (!q) return [];
    return products
      .filter((p) => !p.isCombo && !state.resale[p.itemId] && !hasInventoryLink(p))
      .filter((p) => normalizeText(p.name).includes(q))
      .slice(0, 8);
  }, [products, state.resale, resaleSearch]);

  // ─── Paso 3: recetas ──────────────────────────────────────────────────────
  const setRecipe = (productId, updater) =>
    update((prev) => {
      const current = prev.recipes[productId] || { lines: [], deferred: false };
      return { recipes: { ...prev.recipes, [productId]: updater(current) } };
    });
  const applyRecipeSuggestion = (product) => {
    const suggestion = findRecipeSuggestion(product.name);
    if (!suggestion) return;
    update((prev) => {
      const { lines, addItems } = applySuggestion(suggestion, {
        items: prev.items,
        catalog,
        templates: INVENTORY_TEMPLATES,
      });
      return {
        items: mergeWizardItems(prev.items, addItems),
        recipes: { ...prev.recipes, [product.itemId]: { lines, deferred: false } },
      };
    });
  };

  // ─── Paso 4: stock ────────────────────────────────────────────────────────
  const setStock = (key, rid, value) =>
    update((prev) => ({
      stock: { ...prev.stock, [key]: { ...(prev.stock[key] || {}), [rid]: value } },
    }));
  const applyToAll = (key) =>
    update((prev) => {
      const first = (prev.stock[key] || {})[prev.restaurantIds[0]];
      const row = {};
      prev.restaurantIds.forEach((rid) => {
        row[rid] = first;
      });
      return { stock: { ...prev.stock, [key]: row } };
    });
  const setMin = (key, value) =>
    update((prev) => {
      const minStock = { ...(prev.minStock || {}) };
      if (value === '') delete minStock[key];
      else minStock[key] = value;
      return { minStock };
    });
  const importStockCsv = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const { rows, errors } = parseStockCsv(await readFile(file));
    const known = new Set(toCreate.map((i) => i.key));
    const matched = rows.filter((r) => known.has(r.key));
    update((prev) => {
      const stock = { ...prev.stock };
      const minStock = { ...(prev.minStock || {}) };
      for (const row of matched) {
        if (row.stock !== null) {
          stock[row.key] = { ...(stock[row.key] || {}) };
          prev.restaurantIds.forEach((rid) => {
            stock[row.key][rid] = row.stock;
          });
        }
        if (row.min !== null) minStock[row.key] = row.min;
      }
      return { stock, minStock };
    });
    setCsvMessage({
      scope: 'stock',
      imported: matched.length,
      unknown: rows.length - matched.length,
      errors,
    });
  };

  // ─── Activar ──────────────────────────────────────────────────────────────
  const settingsChanges = () => {
    if (!canEditPolicies || !state.settings) return null;
    const base = settingsFromView(settingsView);
    const changes = {};
    POLICY_KEYS.forEach((key) => {
      if (state.settings[key] !== base[key]) changes[key] = state.settings[key];
    });
    return Object.keys(changes).length ? changes : null;
  };

  const runMenuOps = async (ops) => {
    const result = ops.map((op) => ({ ...op }));
    for (let i = 0; i < result.length; i++) {
      const op = result[i];
      if (op.status === 'done') continue;
      if (op.error) {
        op.status = 'error';
      } else {
        try {
          await updateItem(op.productId, op.data);
          op.status = 'done';
        } catch (error) {
          console.error(`[InventoryWizard] ${op.kind} failed for ${op.productId}:`, error);
          op.status = 'error';
          op.message = inventoryErrorMessage(t, error, 'inventory.wizard.run.opFailed');
        }
      }
      setRun((prev) => ({ ...prev, ops: result.map((o) => ({ ...o })) }));
    }
    return result;
  };

  const finish = (setupResult, ops) => {
    const allOk = setupResult.ok !== false && ops.every((op) => op.status === 'done');
    setRun((prev) => ({ ...prev, running: false, done: true, allOk }));
    if (allOk) {
      storage.remove(draftKey);
      trackEvent('inventory_wizard_finished', {
        items: summary.items,
        links: ops.filter((op) => op.kind === 'link').length,
        recipes: ops.filter((op) => op.kind === 'recipe').length,
        restaurants: summary.restaurants,
        durationMs: Date.now() - (state.startedAt || Date.now()),
      });
    }
  };

  const activate = async () => {
    setRun({ running: true, phase: 'setup', ops: [], setup: null, error: '' });
    let setupResult = run && run.setup && run.setup.ok ? run.setup : null;
    try {
      if (!setupResult) {
        setupResult = await setupInventory(
          buildSetupPayload(state, products, {
            restaurantId: currentRestaurantId,
            settingsChanges: settingsChanges(),
          })
        );
      }
    } catch (error) {
      console.error('[InventoryWizard] setup failed:', error);
      setRun({
        running: false,
        done: true,
        allOk: false,
        phase: 'setup',
        ops: [],
        setup: null,
        error: inventoryErrorMessage(t, error, 'inventory.wizard.run.setupError'),
      });
      return;
    }
    const catalogNow = [...catalog, ...(setupResult.items || [])];
    const previous = new Map(((run && run.ops) || []).map((op) => [op.productId, op]));
    const ops = buildMenuOperations(state, products, {
      itemIds: setupResult.itemIds || {},
      catalog: catalogNow,
    }).map((op) =>
      previous.get(op.productId)?.status === 'done' ? previous.get(op.productId) : op
    );
    setRun((prev) => ({ ...prev, phase: 'menu', setup: setupResult, ops }));
    const done = await runMenuOps(ops);
    finish(setupResult, done);
  };

  const retryOp = async (productId) => {
    const ops = run.ops.map((op) =>
      op.productId === productId && !op.error ? { ...op, status: 'pending' } : op
    );
    setRun((prev) => ({ ...prev, running: true, ops }));
    const done = await runMenuOps(ops);
    finish(run.setup || { ok: false }, done);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const renderStepper = () => (
    <ol className="inv-wizard__steps" aria-label={t('inventory.wizard.stepsLabel')}>
      {visibleSteps.map(({ key, index }, position) => {
        const current = visibleSteps.findIndex((s) => s.index === state.step);
        const status = position < current ? 'done' : position === current ? 'active' : 'pending';
        return (
          <li
            key={key}
            className={`inv-wizard__step inv-wizard__step--${status}`}
            aria-current={status === 'active' ? 'step' : undefined}
          >
            <button
              type="button"
              className="inv-wizard__step-btn"
              disabled={position > current || Boolean(run && run.running)}
              onClick={() => goTo(index)}
            >
              <span className="inv-wizard__step-num">
                {status === 'done' ? <Check size={14} aria-hidden="true" /> : position + 1}
              </span>
              <span className="inv-wizard__step-label">{t(`inventory.wizard.steps.${key}`)}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const renderScope = () => (
    <section className="inv-wizard__panel">
      <h2>{t('inventory.wizard.scope.title')}</h2>
      <p className="inv-wizard__hint">{t('inventory.wizard.scope.hint')}</p>
      <div className="inv-wizard__checklist">
        {restaurants.map((r) => (
          <label key={r.restaurantId} className="inv-wizard__check">
            <input
              type="checkbox"
              checked={state.restaurantIds.includes(r.restaurantId)}
              onChange={() =>
                update((prev) => ({
                  restaurantIds: prev.restaurantIds.includes(r.restaurantId)
                    ? prev.restaurantIds.filter((id) => id !== r.restaurantId)
                    : [...prev.restaurantIds, r.restaurantId],
                }))
              }
            />
            <span>{r.name || r.restaurantId}</span>
          </label>
        ))}
      </div>
    </section>
  );

  const renderItems = () => {
    const filtered = filterWizardItems(state.items, itemSearch);
    const selectedCount = state.items.filter((i) => i.selected).length;
    return (
      <section className="inv-wizard__panel">
        <h2>{t('inventory.wizard.items.title')}</h2>
        <p className="inv-wizard__hint">{t('inventory.wizard.items.hint')}</p>
        <TemplatePicker
          title={t('inventory.wizard.items.rubrosTitle')}
          hint={t('inventory.wizard.items.rubrosHint')}
          templates={INVENTORY_TEMPLATES.map((tpl) => {
            const Icon = RUBRO_ICONS[tpl.icon] || Pizza;
            return {
              id: tpl.id,
              icon: <Icon size={22} aria-hidden="true" />,
              name: t(`inventory.wizard.rubros.${tpl.id}.name`),
              description: t(`inventory.wizard.rubros.${tpl.id}.description`),
              chips: [
                t('inventory.wizard.items.itemsCount', { count: tpl.items.length }),
                ...(state.rubroId === tpl.id ? [t('inventory.wizard.items.chosen')] : []),
              ],
            };
          })}
          onSelect={chooseRubro}
        />

        <div className="inv-wizard__toolbar">
          <label className="inv-wizard__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder={t('inventory.wizard.items.searchPlaceholder')}
              aria-label={t('inventory.wizard.items.searchPlaceholder')}
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              setCustomItem({ name: '', unit: 'un', category: '', kind: 'INGREDIENT' })
            }
          >
            <Plus size={16} aria-hidden="true" /> {t('inventory.wizard.items.addCustom')}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => itemsCsvRef.current && itemsCsvRef.current.click()}
          >
            <Upload size={16} aria-hidden="true" /> {t('inventory.wizard.items.importCsv')}
          </button>
          <input
            ref={itemsCsvRef}
            type="file"
            accept=".csv,text/csv"
            className="inv-wizard__file"
            onChange={importItemsCsv}
            aria-label={t('inventory.wizard.items.importCsv')}
          />
        </div>
        <p className="inv-wizard__hint inv-wizard__hint--small">
          {t('inventory.wizard.items.csvHint')}
        </p>
        {renderCsvMessage('items')}

        {state.items.length > 0 && (
          <div className="inv-wizard__bulk">
            <span>{t('inventory.wizard.items.selectedCount', { count: selectedCount })}</span>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setAllItems(true)}>
              {t('inventory.wizard.items.selectAll')}
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() => setAllItems(false)}
            >
              {t('inventory.wizard.items.selectNone')}
            </button>
          </div>
        )}

        {state.items.length === 0 ? (
          <p className="inv-wizard__empty">{t('inventory.wizard.items.empty')}</p>
        ) : (
          groupByCategory(filtered).map((group) => (
            <fieldset key={group.category || 'none'} className="inv-wizard__group">
              <legend>{group.category || t('inventory.wizard.items.noCategory')}</legend>
              {group.items.map((item) => (
                <label key={item.key} className="inv-wizard__item">
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={() => toggleItem(item.key)}
                  />
                  <span className="inv-wizard__item-name">{item.name}</span>
                  <span className="inv-wizard__chip">{item.unit}</span>
                  <span className="inv-wizard__chip inv-wizard__chip--muted">
                    {t(`inventory.kinds.${item.kind}`)}
                  </span>
                  {catalogKeys.has(item.key) && (
                    <span className="inv-wizard__chip inv-wizard__chip--ok">
                      {t('inventory.wizard.items.alreadyInCatalog')}
                    </span>
                  )}
                </label>
              ))}
            </fieldset>
          ))
        )}
      </section>
    );
  };

  function renderCsvMessage(scope) {
    if (!csvMessage || csvMessage.scope !== scope) return null;
    return (
      <div className="inv-wizard__notice" role="status">
        {t('inventory.wizard.csv.imported', { count: csvMessage.imported })}
        {csvMessage.unknown > 0 &&
          ` ${t('inventory.wizard.csv.unknown', { count: csvMessage.unknown })}`}
        {csvMessage.errors.length > 0 && (
          <ul>
            {csvMessage.errors.slice(0, 5).map((err) => (
              <li key={`${err.line}-${err.code}`}>
                {t(`inventory.wizard.csv.errors.${err.code}`, {
                  line: err.line,
                  value: err.value || '',
                })}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const renderResale = () => (
    <section className="inv-wizard__panel">
      <h2>{t('inventory.wizard.resale.title')}</h2>
      <p className="inv-wizard__hint">{t('inventory.wizard.resale.hint')}</p>
      {resaleProducts.length === 0 && (
        <p className="inv-wizard__empty">{t('inventory.wizard.resale.noneDetected')}</p>
      )}
      {resaleProducts.length > 0 && (
        <div className="inv-wizard__table-wrap">
          <table className="inv-wizard__table">
            <thead>
              <tr>
                <th scope="col">{t('inventory.wizard.resale.control')}</th>
                <th scope="col">{t('inventory.wizard.resale.product')}</th>
                <th scope="col">{t('inventory.wizard.resale.perSale')}</th>
                <th scope="col">{t('inventory.wizard.resale.target')}</th>
              </tr>
            </thead>
            <tbody>
              {resaleProducts.map((product) => {
                if (hasInventoryLink(product)) {
                  return (
                    <tr key={product.itemId} className="inv-wizard__row--muted">
                      <td>
                        <Check size={16} aria-hidden="true" />
                      </td>
                      <td>
                        {product.name}
                        <span className="inv-wizard__sub">{product.categoryName}</span>
                      </td>
                      <td colSpan={2}>
                        <span className="inv-wizard__chip inv-wizard__chip--ok">
                          {t('inventory.wizard.resale.alreadyControlled')}
                        </span>
                      </td>
                    </tr>
                  );
                }
                const row = state.resale[product.itemId];
                return (
                  <tr key={product.itemId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(row.selected)}
                        onChange={() => setResale(product.itemId, { selected: !row.selected })}
                        aria-label={t('inventory.wizard.resale.controlProduct', {
                          name: product.name,
                        })}
                      />
                    </td>
                    <td>
                      {product.name}
                      <span className="inv-wizard__sub">{product.categoryName}</span>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0.001"
                        step="any"
                        className="inv-wizard__num"
                        value={row.quantity}
                        disabled={!row.selected}
                        onChange={(e) => setResale(product.itemId, { quantity: e.target.value })}
                        aria-label={t('inventory.wizard.resale.perSale')}
                      />{' '}
                      un
                    </td>
                    <td>
                      <select
                        value={row.target}
                        disabled={!row.selected}
                        onChange={(e) => setResale(product.itemId, { target: e.target.value })}
                        aria-label={t('inventory.wizard.resale.target')}
                      >
                        <option value={MIRROR_TARGET}>{t('inventory.wizard.resale.mirror')}</option>
                        {options.map((opt) => (
                          <option key={opt.ref} value={opt.ref}>
                            {opt.name} ({opt.unit})
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="inv-wizard__add-product">
        <label className="inv-wizard__search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={resaleSearch}
            onChange={(e) => setResaleSearch(e.target.value)}
            placeholder={t('inventory.wizard.resale.searchPlaceholder')}
            aria-label={t('inventory.wizard.resale.searchPlaceholder')}
          />
        </label>
        {resaleMatches.length > 0 && (
          <ul className="inv-wizard__matches">
            {resaleMatches.map((product) => (
              <li key={product.itemId}>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={() => {
                    setResale(product.itemId, { selected: true });
                    setResaleSearch('');
                  }}
                >
                  <Plus size={14} aria-hidden="true" /> {product.name}
                  <span className="inv-wizard__sub">{product.categoryName}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  const renderRecipeCard = (product) => {
    const recipe = state.recipes[product.itemId] || { lines: [], deferred: false };
    const suggestion = findRecipeSuggestion(product.name);
    return (
      <article key={product.itemId} className="inv-wizard__recipe">
        <header>
          <h3>{product.name}</h3>
          <span className="inv-wizard__sub">{product.categoryName}</span>
          {suggestion && !recipe.deferred && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => applyRecipeSuggestion(product)}
            >
              <Wand2 size={14} aria-hidden="true" /> {t('inventory.wizard.recipes.useSuggestion')}
            </button>
          )}
          <label className="inv-wizard__check inv-wizard__check--inline">
            <input
              type="checkbox"
              checked={recipe.deferred}
              onChange={() => setRecipe(product.itemId, (r) => ({ ...r, deferred: !r.deferred }))}
            />
            <span>{t('inventory.wizard.recipes.later')}</span>
          </label>
        </header>
        {!recipe.deferred && (
          <>
            {recipe.lines.map((line, index) => {
              const option = optionByRef.get(line.ref);
              const units = option ? familyUnits(option.unit) : CANONICAL_UNITS;
              return (
                <div key={`${product.itemId}-${index}`} className="inv-wizard__line">
                  <select
                    value={line.ref}
                    onChange={(e) =>
                      setRecipe(product.itemId, (r) => {
                        const opt = optionByRef.get(e.target.value);
                        const lines = r.lines.map((l, i) =>
                          i === index
                            ? {
                                ...l,
                                ref: e.target.value,
                                unit:
                                  opt && familyUnits(opt.unit).includes(l.unit)
                                    ? l.unit
                                    : (opt || {}).unit,
                              }
                            : l
                        );
                        return { ...r, lines };
                      })
                    }
                    aria-label={t('menu.recipe.ingredientLabel')}
                  >
                    <option value="">{t('menu.recipe.selectIngredient')}</option>
                    {options.map((opt) => (
                      <option key={opt.ref} value={opt.ref}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="inv-wizard__num"
                    value={line.quantity}
                    onChange={(e) =>
                      setRecipe(product.itemId, (r) => ({
                        ...r,
                        lines: r.lines.map((l, i) =>
                          i === index ? { ...l, quantity: e.target.value } : l
                        ),
                      }))
                    }
                    aria-label={t('menu.recipe.quantity')}
                  />
                  <select
                    value={line.unit || ''}
                    onChange={(e) =>
                      setRecipe(product.itemId, (r) => ({
                        ...r,
                        lines: r.lines.map((l, i) =>
                          i === index ? { ...l, unit: e.target.value } : l
                        ),
                      }))
                    }
                    aria-label={t('menu.recipe.unit')}
                  >
                    {units.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      setRecipe(product.itemId, (r) => ({
                        ...r,
                        lines: r.lines.filter((_, i) => i !== index),
                      }))
                    }
                    aria-label={t('menu.recipe.removeIngredient')}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() =>
                setRecipe(product.itemId, (r) => ({
                  ...r,
                  lines: [...r.lines, { ref: '', quantity: '', unit: '' }],
                }))
              }
            >
              <Plus size={14} aria-hidden="true" /> {t('inventory.wizard.recipes.addLine')}
            </button>
          </>
        )}
      </article>
    );
  };

  const renderRecipes = () => {
    if (!recipesGate.loading && !recipesGate.enabled) {
      return (
        <section className="inv-wizard__panel inv-wizard__locked">
          <Lock size={28} aria-hidden="true" />
          <h2>{t('inventory.wizard.recipes.lockedTitle')}</h2>
          <p className="inv-wizard__hint">{t('inventory.wizard.recipes.lockedHint')}</p>
          <button type="button" className="btn btn-primary" onClick={recipesGate.open}>
            {t('inventory.wizard.recipes.unlock')}
          </button>
        </section>
      );
    }
    const visible = showAllRecipes ? candidates : candidates.slice(0, EXPANDED_RECIPES);
    return (
      <section className="inv-wizard__panel">
        <h2>{t('inventory.wizard.recipes.title')}</h2>
        <p className="inv-wizard__hint">{t('inventory.wizard.recipes.hint')}</p>
        {candidates.length === 0 && (
          <p className="inv-wizard__empty">{t('inventory.wizard.recipes.none')}</p>
        )}
        {visible.map(renderRecipeCard)}
        {!showAllRecipes && candidates.length > EXPANDED_RECIPES && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowAllRecipes(true)}
          >
            {t('inventory.wizard.recipes.showAll', { count: candidates.length })}
          </button>
        )}
        {products.some((p) => hasRecipe(p)) && (
          <p className="inv-wizard__hint inv-wizard__hint--small">
            {t('inventory.wizard.recipes.existingNote', {
              count: products.filter((p) => hasRecipe(p)).length,
            })}
          </p>
        )}
      </section>
    );
  };

  const renderStock = () => (
    <section className="inv-wizard__panel">
      <h2>{t('inventory.wizard.stock.title')}</h2>
      <p className="inv-wizard__hint">{t('inventory.wizard.stock.hint')}</p>
      <div className="inv-wizard__toolbar">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => stockCsvRef.current && stockCsvRef.current.click()}
        >
          <Upload size={16} aria-hidden="true" /> {t('inventory.wizard.stock.importCsv')}
        </button>
        <input
          ref={stockCsvRef}
          type="file"
          accept=".csv,text/csv"
          className="inv-wizard__file"
          onChange={importStockCsv}
          aria-label={t('inventory.wizard.stock.importCsv')}
        />
        <span className="inv-wizard__hint inv-wizard__hint--small">
          {t('inventory.wizard.stock.csvHint')}
        </span>
      </div>
      {renderCsvMessage('stock')}
      <div className="inv-wizard__table-wrap">
        <table className="inv-wizard__table">
          <thead>
            <tr>
              <th scope="col">{t('inventory.wizard.stock.item')}</th>
              {state.restaurantIds.map((rid) => (
                <th key={rid} scope="col">
                  {state.restaurantIds.length > 1
                    ? restaurantName(rid)
                    : t('inventory.wizard.stock.initial')}
                </th>
              ))}
              {state.restaurantIds.length > 1 && (
                <th scope="col" aria-label={t('inventory.wizard.stock.applyAll')} />
              )}
              <th scope="col">{t('inventory.wizard.stock.min')}</th>
            </tr>
          </thead>
          <tbody>
            {toCreate.map((item) => (
              <tr key={item.key}>
                <td>
                  {item.name}
                  <span className="inv-wizard__sub">{item.unit}</span>
                </td>
                {state.restaurantIds.map((rid) => (
                  <td key={rid}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className="inv-wizard__num"
                      value={(state.stock[item.key] || {})[rid] ?? ''}
                      onChange={(e) => setStock(item.key, rid, e.target.value)}
                      aria-label={t('inventory.wizard.stock.stockFor', {
                        item: item.name,
                        restaurant: restaurantName(rid),
                      })}
                    />
                  </td>
                ))}
                {state.restaurantIds.length > 1 && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => applyToAll(item.key)}
                    >
                      {t('inventory.wizard.stock.applyAll')}
                    </button>
                  </td>
                )}
                <td>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className="inv-wizard__num"
                    value={(state.minStock || {})[item.key] ?? item.defaultMinStock}
                    onChange={(e) => setMin(item.key, e.target.value)}
                    aria-label={t('inventory.wizard.stock.minFor', { item: item.name })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="inv-wizard__hint inv-wizard__hint--small">
        {t('inventory.wizard.stock.existingNote')}
      </p>
    </section>
  );

  const renderPolicies = () => {
    const settings = state.settings || settingsFromView(settingsView);
    const setPolicy = (key, value) =>
      update((prev) => ({ settings: { ...settings, ...prev.settings, [key]: value } }));
    const p = 'settings.inventoryPolicies';
    return (
      <section className="inv-wizard__panel">
        <h2>{t('inventory.wizard.policies.title')}</h2>
        <p className="inv-wizard__hint">
          {canEditPolicies ? t('inventory.wizard.policies.hint') : t(`${p}.definedByOrg`)}
        </p>
        <div className="inv-wizard__policy">
          <label htmlFor="inv-wizard-trigger">{t('inventory.wizard.policies.whenQuestion')}</label>
          <select
            id="inv-wizard-trigger"
            value={settings.deductionTrigger}
            disabled={!canEditPolicies}
            onChange={(e) => setPolicy('deductionTrigger', e.target.value)}
          >
            {DEDUCTION_TRIGGERS.map((opt) => (
              <option key={opt} value={opt}>
                {t(`${p}.deductionTrigger.options.${opt}`)}
              </option>
            ))}
          </select>
          <p className="inv-wizard__hint inv-wizard__hint--small">
            {t(`inventory.wizard.policies.triggerHelp.${settings.deductionTrigger}`)}
          </p>
        </div>
        {['restockOnCancel', 'allowNegativeStock'].map((key) => (
          <div key={key} className="inv-wizard__policy">
            <label className="inv-wizard__check">
              <input
                type="checkbox"
                checked={Boolean(settings[key])}
                disabled={!canEditPolicies}
                onChange={() => setPolicy(key, !settings[key])}
              />
              <span>{t(`${p}.${key}.label`)}</span>
            </label>
            <p className="inv-wizard__hint inv-wizard__hint--small">{t(`${p}.${key}.help`)}</p>
          </div>
        ))}
        <div className="inv-wizard__policy">
          <label htmlFor="inv-wizard-costing">{t(`${p}.costingMethod.label`)}</label>
          <select
            id="inv-wizard-costing"
            value={settings.costingMethod}
            disabled={!canEditPolicies}
            onChange={(e) => setPolicy('costingMethod', e.target.value)}
          >
            {COSTING_METHODS.map((opt) => (
              <option key={opt} value={opt}>
                {t(`${p}.costingMethod.options.${opt}`)}
              </option>
            ))}
          </select>
          <p className="inv-wizard__hint inv-wizard__hint--small">{t(`${p}.costingMethod.help`)}</p>
        </div>
      </section>
    );
  };

  const renderRun = () => {
    const ops = run.ops || [];
    const doneCount = ops.filter((op) => op.status === 'done').length;
    const pct = ops.length ? Math.round((doneCount / ops.length) * 100) : run.done ? 100 : 0;
    const setupErrors = (run.setup && run.setup.errors) || [];
    return (
      <div className="inv-wizard__run" aria-live="polite">
        {run.running && <p>{t(`inventory.wizard.run.phase.${run.phase}`)}</p>}
        {ops.length > 0 && (
          <div
            className="inv-wizard__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={t('inventory.wizard.run.progress')}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
        )}
        {run.error && (
          <p className="inv-wizard__error" role="alert">
            <AlertTriangle size={16} aria-hidden="true" /> {run.error}
          </p>
        )}
        {setupErrors.length > 0 && (
          <div className="inv-wizard__error" role="alert">
            <p>{t('inventory.wizard.run.setupPending', { count: setupErrors.length })}</p>
            <ul>
              {setupErrors.slice(0, 10).map((err, index) => (
                <li key={`${err.step}-${index}`}>
                  {t(`inventory.wizard.run.errorStep.${err.step}`)}:{' '}
                  {err.name || err.key || err.itemId || ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ops.some((op) => op.status === 'error') && (
          <ul className="inv-wizard__ops">
            {ops
              .filter((op) => op.status === 'error')
              .map((op) => (
                <li key={op.productId}>
                  <span>
                    {t(`inventory.wizard.run.opKind.${op.kind}`)} · {op.productName}
                    {op.error && ` — ${t('inventory.wizard.run.unresolved')}`}
                    {op.message && ` — ${op.message}`}
                  </span>
                  {!op.error && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={run.running}
                      onClick={() => retryOp(op.productId)}
                    >
                      <RotateCcw size={14} aria-hidden="true" /> {t('common.retry')}
                    </button>
                  )}
                </li>
              ))}
          </ul>
        )}
        {run.done && !run.allOk && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={activate}
            disabled={run.running}
          >
            <RotateCcw size={16} aria-hidden="true" /> {t('inventory.wizard.run.retryAll')}
          </button>
        )}
        {finished && (
          <div className="inv-wizard__success">
            <Check size={28} aria-hidden="true" />
            <h3>{t('inventory.wizard.run.successTitle')}</h3>
            <p>{t('inventory.wizard.run.successHint')}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/inventory')}
            >
              {t('inventory.wizard.run.goToInventory')}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderSummary = () => (
    <section className="inv-wizard__panel">
      <h2>{t('inventory.wizard.summary.title')}</h2>
      <ul className="inv-wizard__summary">
        <li>{t('inventory.wizard.summary.items', { count: summary.items })}</li>
        <li>{t('inventory.wizard.summary.links', { count: summary.links })}</li>
        <li>{t('inventory.wizard.summary.recipes', { count: summary.recipes })}</li>
        <li>{t('inventory.wizard.summary.restaurants', { count: summary.restaurants })}</li>
        {summary.deferred > 0 && (
          <li>{t('inventory.wizard.summary.deferred', { count: summary.deferred })}</li>
        )}
      </ul>
      <p className="inv-wizard__hint inv-wizard__hint--small">
        {t('inventory.wizard.summary.idempotentNote')}
      </p>
      {!run && (
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={activate}
          disabled={!summary.items}
        >
          {t('inventory.wizard.summary.activate')}
        </button>
      )}
      {run && renderRun()}
    </section>
  );

  const renderStep = () => {
    switch (stepKey) {
      case 'scope':
        return renderScope();
      case 'items':
        return renderItems();
      case 'resale':
        return renderResale();
      case 'recipes':
        return renderRecipes();
      case 'stock':
        return renderStock();
      case 'policies':
        return renderPolicies();
      default:
        return renderSummary();
    }
  };

  if (loading) {
    return (
      <div className="inv-wizard">
        <p className="inv-wizard__loading">{t('common.loading')}</p>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="inv-wizard">
        <p className="inv-wizard__error" role="alert">
          {loadError}
        </p>
      </div>
    );
  }

  const position = visibleSteps.findIndex((s) => s.index === state.step);
  const isLast = stepKey === 'summary';
  return (
    <div className="inv-wizard">
      <header className="inv-wizard__header">
        <div>
          <h1>{t('inventory.wizard.title')}</h1>
          <p className="inv-wizard__hint">
            {isChain ? t('inventory.wizard.subtitleChain') : t('inventory.wizard.subtitle')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={exit}
          disabled={Boolean(run && run.running)}
        >
          {t('inventory.wizard.exit')}
        </button>
      </header>

      {showDraftBanner && (
        <div className="inv-wizard__notice" role="status">
          <span>{t('inventory.wizard.draftRestored')}</span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setShowDraftBanner(false)}
          >
            {t('inventory.wizard.keepDraft')}
          </button>
          <button type="button" className="btn btn-link btn-sm" onClick={startOver}>
            {t('inventory.wizard.startOver')}
          </button>
        </div>
      )}

      {renderStepper()}
      {renderStep()}

      {navError && (
        <p className="inv-wizard__error" role="alert">
          {navError}
        </p>
      )}

      {!(run && (run.running || finished)) && (
        <footer className="inv-wizard__footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={back}
            disabled={position <= 0}
          >
            <ChevronLeft size={16} aria-hidden="true" /> {t('common.back')}
          </button>
          {!isLast && (
            <button type="button" className="btn btn-primary" onClick={next}>
              {t('common.next')} <ChevronRight size={16} aria-hidden="true" />
            </button>
          )}
        </footer>
      )}

      {customItem && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="inv-wizard-custom-title"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="inv-wizard-custom-title">{t('inventory.wizard.items.addCustom')}</h2>
            </div>
            <div className="modal-body inv-wizard__form">
              <label htmlFor="inv-wizard-custom-name">{t('inventory.name')}</label>
              <input
                id="inv-wizard-custom-name"
                value={customItem.name}
                onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                autoFocus
              />
              <label htmlFor="inv-wizard-custom-unit">{t('inventory.unit')}</label>
              <select
                id="inv-wizard-custom-unit"
                value={customItem.unit}
                onChange={(e) => setCustomItem({ ...customItem, unit: e.target.value })}
              >
                {CANONICAL_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
              <label htmlFor="inv-wizard-custom-kind">{t('inventory.kind')}</label>
              <select
                id="inv-wizard-custom-kind"
                value={customItem.kind}
                onChange={(e) => setCustomItem({ ...customItem, kind: e.target.value })}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`inventory.kinds.${k}`)}
                  </option>
                ))}
              </select>
              <label htmlFor="inv-wizard-custom-category">{t('inventory.category')}</label>
              <input
                id="inv-wizard-custom-category"
                value={customItem.category}
                onChange={(e) => setCustomItem({ ...customItem, category: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setCustomItem(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveCustomItem}
                disabled={!customItem.name.trim()}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="inv-wizard-leave-title"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="inv-wizard-leave-title">{t('inventory.wizard.leaveTitle')}</h2>
            </div>
            <div className="modal-body">
              <p>{t('inventory.wizard.leaveMessage')}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelNavigation}>
                {t('common.unsavedChangesStay')}
              </button>
              <button type="button" className="btn btn-primary" onClick={confirmNavigation}>
                {t('inventory.wizard.leaveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventorySetupWizard;
